import React from 'react'
import { useTranslation } from 'react-i18next'
import { ShieldAlert, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/AuthContext'
import { useFormatters } from '@/lib/hooks/useFormatters'
import { useRoute } from '@/lib/RouteContext'
import { ROUTES } from '@/lib/routes'
import { TWO_FACTOR_REQUIRED_EVENT } from '@/lib/twoFactor/stepUpBridge'
import { cn } from '@/lib/utils'

const DISMISS_KEY = 'memberfy_2fa_prompt_dismissed'

/**
 * F-233: the account must have two-step verification here and has not turned
 * it on (`/auth/me` › `twoFactor.setupRequired`).
 * - Team and SuperAdmin, inside the 7 days: a warning with the deadline.
 * - Past it (or a 403 `TWO_FACTOR_REQUIRED` just came back): the blocking
 *   banner, not dismissible; the team screens and actions refuse until it is on.
 * - A member where the owner turned the default on: an invitation, dismissible
 *   for this tab (members are asked, never blocked).
 */
export const TwoFactorBanner = () => {
  const { t } = useTranslation('twoFactor')
  const { activeMember } = useAuth()
  const { formatLongDate } = useFormatters()
  const { navigate, currentPath } = useRoute()
  const [refused, setRefused] = React.useState(false)
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1' } catch { return false }
  })
  const summary = activeMember?.twoFactor

  React.useEffect(() => {
    const onRequired = () => setRefused(true)
    window.addEventListener(TWO_FACTOR_REQUIRED_EVENT, onRequired)
    return () => window.removeEventListener(TWO_FACTOR_REQUIRED_EVENT, onRequired)
  }, [])

  if (summary?.enabled) return null
  if (!summary?.setupRequired && !refused) return null

  const onSecurity = currentPath.startsWith('/account') && new URLSearchParams(window.location.search).get('tab') === 'security'
  const deadline = summary?.deadline ? new Date(summary.deadline) : null
  const blocked = refused || (summary?.requiredReason !== 'community' && deadline !== null && deadline.getTime() <= Date.now())
  const kind: 'blocked' | 'grace' | 'invite' = blocked ? 'blocked' : summary?.requiredReason === 'community' ? 'invite' : 'grace'
  if (kind === 'invite' && dismissed) return null

  const text = kind === 'blocked'
    ? t('banner.blocked')
    : kind === 'grace'
      ? (deadline ? t('banner.grace', { date: formatLongDate(deadline) }) : t('banner.graceNoDate'))
      : t('banner.invite')

  return (
    <div role={kind === 'blocked' ? 'alert' : 'status'} data-testid={`two-factor-banner-${kind}`}
      className={cn('border-b border-border px-4 py-3 text-sm sm:px-6',
        kind === 'blocked' ? 'bg-danger-surface text-danger-text' : kind === 'grace' ? 'bg-warning-surface text-warning-text' : 'bg-info-surface text-info-text')}>
      <div className="mx-auto flex max-w-7xl flex-col gap-2 sm:flex-row sm:items-center">
        <p className="flex min-w-0 flex-1 items-start gap-2">
          {kind === 'invite' ? <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" /> : <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />}
          <span>{text}</span>
        </p>
        <div className="flex flex-shrink-0 items-center gap-2">
          {!onSecurity && (
            <Button type="button" variant="outline" size="sm" onClick={() => navigate(ROUTES.accountSecurity)} data-testid="two-factor-banner-setup">
              {t('banner.setup')}
            </Button>
          )}
          {kind === 'invite' && (
            <button type="button" aria-label={t('banner.dismiss')} className="rounded p-1 hover:bg-black/5"
              onClick={() => { setDismissed(true); try { sessionStorage.setItem(DISMISS_KEY, '1') } catch { /* this render only */ } }}>
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
